import { C } from '@parsina/framework/clinical/src/constants/theme';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

/**
 * Segmented pill control — the app's one filter/tab switcher, replacing the five
 * hand-rolled pill groups (Accounting settlement, Encounters status, Credits
 * type, Profile tabs, view toggles). RTL-native; active pill = white + navy.
 *
 * Props:
 *   items    — [{ id, label, icon?, ariaLabel?, dot? }] — ariaLabel is required
 *              when a segment is icon-only (no visible label to name it, §12.1).
 *              `dot` marks a segment as needing attention (e.g. a form with
 *              unsaved changes) and is announced, not colour-only (§12.4).
 *   value    — active id
 *   onChange — (id) => void
 *   size     — 'sm' | 'md' (default 'md')
 *   ariaLabel — accessible name for the group
 *   testId   — data-testid for the group; segments get `${testId}-${id}` (§15.1)
 */
export default function SegmentedControl({ items = [], value, onChange, size = 'md', ariaLabel, testId }) {
  const { t, isRTL } = useTranslation();
  const pad = size === 'sm' ? '6px 12px' : '8px 16px';
  const font = size === 'sm' ? '0.75rem' : '0.8125rem';

  const onKeyDown = (e, idx) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    // The arrows follow what the doctor SEES, so which one advances depends on
    // the direction the strip is laid out in — not on the array order.
    const advances = isRTL ? 'ArrowLeft' : 'ArrowRight';
    const dir = e.key === advances ? 1 : -1;
    const next = (idx + dir + items.length) % items.length;
    onChange?.(items[next].id);
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      data-testid={testId}
      style={{
        display: 'inline-flex',
        // A strip sized for Persian is not wide enough for the same strip in
        // English — «امنیت» is four glyphs, "Security" is eight — so the nine
        // settings sections overran their rail and the last one sat off-screen
        // behind a scroll with nothing to say so. Wrapping costs nothing while
        // the segments fit (which is every case that fits today) and keeps
        // every segment REACHABLE when a longer language means they do not.
        flexWrap: 'wrap',
        maxWidth: '100%',
        background: C.bg,
        borderRadius: 10,
        padding: 3,
        gap: 2,
      }}
    >
      {items.map((item, idx) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            role="tab"
            aria-selected={active}
            aria-label={item.ariaLabel}
            title={item.ariaLabel}
            data-testid={testId ? `${testId}-${item.id}` : undefined}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange?.(item.id)}
            onKeyDown={(e) => onKeyDown(e, idx)}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              // A segment is a touch target wherever this control renders, and
              // it renders on the phone (Messages, Credits, Accounting filters).
              // 2.75rem is the floor (DESIGN-CONCEPT-MOBILE §6); the padding
              // above still sets the horizontal rhythm.
              minHeight: '2.75rem',
              padding: pad,
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: font,
              fontWeight: active ? 700 : 500,
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
              background: active ? '#fff' : 'transparent',
              color: active ? C.navy : C.textSecondary,
              boxShadow: active ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            {item.icon}
            {item.label}
            {item.dot && (
              <>
                <span
                  aria-hidden
                  data-testid={testId ? `${testId}-${item.id}-dot` : undefined}
                  style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: C.amber, flexShrink: 0,
                  }}
                />
                <span className="sr-only">{t('settings.profile.unsavedTitle')}</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
